import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { isMobileNative } from 'app/utils/web-helpers';

import React, { useEffect } from 'react';
import { Pressable, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const QUICK_REACTIONS = ['❤️', '😆', '😮', '😢', '😡', '👍'];

const SPRING_CONFIG = { damping: 12, stiffness: 200, mass: 0.4 };
const STAGGER_DELAY = 30;
const ReactionAnimatedView = isMobileNative ? Animated.View : View;

interface EmojiReactionBarProps {
  onSelect: (emoji: string) => void;
  currentEmojis?: string[];
}

function AnimatedEmoji({
  emoji,
  index,
  onSelect,
  isSelected,
}: {
  emoji: string;
  index: number;
  onSelect: () => void;
  isSelected: boolean;
}) {
  const scale = useSharedValue(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (!isMobileNative) return;

    const timeout = setTimeout(() => {
      scale.set(withSpring(1, SPRING_CONFIG));
      opacity.set(withSpring(1, SPRING_CONFIG));
    }, index * STAGGER_DELAY);
    return () => clearTimeout(timeout);
  }, [index, scale, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.get() }],
    opacity: opacity.get(),
  }));

  return (
    <ReactionAnimatedView style={isMobileNative ? animatedStyle : undefined}>
      <Pressable onPress={onSelect} style={{ alignItems: 'center' }}>
        <Text style={{ fontSize: 28 }}>{emoji}</Text>
        {isSelected ? (
          <View
            style={{
              position: 'absolute',
              bottom: -theme.spacing['spacing.general.x1'],
              width: 6,
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.colors['surface.brand.secondary-5.subtle'],
              marginTop: 2,
            }}
          />
        ) : null}
      </Pressable>
    </ReactionAnimatedView>
  );
}

export function EmojiReactionBar({ onSelect, currentEmojis }: EmojiReactionBarProps) {
  const containerScale = useSharedValue(0.8);
  const containerOpacity = useSharedValue(0);

  useEffect(() => {
    if (!isMobileNative) return;

    containerScale.set(withSpring(1, { damping: 14, stiffness: 180, mass: 0.5 }));
    containerOpacity.set(withSpring(1, { damping: 14, stiffness: 180, mass: 0.5 }));
  }, [containerScale, containerOpacity]);

  const containerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: containerScale.get() }],
    opacity: containerOpacity.get(),
  }));

  return (
    <ReactionAnimatedView style={isMobileNative ? containerStyle : undefined}>
      <Row
        alignItems="center"
        gap="spacing.general.x3"
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          borderRadius: 24,
          paddingHorizontal: theme.spacing['spacing.general.x3'],
          paddingVertical:
            currentEmojis && currentEmojis.length > 0
              ? theme.spacing['spacing.general.x2']
              : theme.spacing['spacing.general.x1'],
        }}
      >
        {QUICK_REACTIONS.map((emoji, index) => (
          <AnimatedEmoji
            key={emoji}
            emoji={emoji}
            index={index}
            onSelect={() => onSelect(emoji)}
            isSelected={currentEmojis?.includes(emoji) || false}
          />
        ))}
      </Row>
    </ReactionAnimatedView>
  );
}
